import React from "react";
import { motion } from "framer-motion";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";

const ContactPage = () => {
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-red-700 via-black to-blue-800 text-white flex flex-col items-center justify-center px-6 py-16 overflow-hidden">
      {/* Animated gradient glows */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute w-[30rem] h-[30rem] bg-blue-600/40 rounded-full blur-3xl"
          animate={{ x: [0, 100, -100, 0], y: [0, -50, 50, 0] }}
          transition={{ repeat: Infinity, duration: 25 }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-[35rem] h-[35rem] bg-red-600/40 rounded-full blur-3xl"
          animate={{ x: [0, -100, 100, 0], y: [0, 50, -50, 0] }}
          transition={{ repeat: Infinity, duration: 30 }}
        />
      </div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-4xl md:text-5xl font-extrabold text-center mb-12 z-10 drop-shadow-lg"
      >
        <span className="text-blue-300">Contact </span> MAD Academy
      </motion.h1>

      {/* Main Grid */}
      <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-10 z-10">
        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-b from-red-800/60 to-blue-800/60 rounded-2xl p-8 shadow-2xl backdrop-blur-lg"
        >
          <h2 className="text-2xl font-semibold mb-6 border-b border-yellow-400 pb-2">
            Contact Information
          </h2>

          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <FaPhoneAlt className="text-blue-400 text-xl" />
              <div>
                <p className="font-semibold">Phone</p>
                <p className="text-gray-300">+91 98765 43210</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <FaEnvelope className="text-blue-400 text-xl" />
              <div>
                <p className="font-semibold">Email</p>
                <p className="text-gray-300">contact@madacademy.com</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <FaMapMarkerAlt className="text-blue-400 text-xl" />
              <div>
                <p className="font-semibold">Address</p>
                <p className="text-gray-300">
                  42 Innovation Park, Hyderabad, India
                </p>
              </div>
            </div>
          </div>

          {/* Embedded Google Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-8 rounded-xl overflow-hidden border-2 border-yellow-400 shadow-lg"
          >
            <iframe
              title="MAD Academy Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.3852359752533!2d78.38261837493472!3d17.437462983430716!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93d26a8a29cf%3A0xc4b27a0d5c1f6cf1!2sHyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1696999950000!5m2!1sen!2sin"
              width="100%"
              height="250"
              allowFullScreen=""
              loading="lazy"
              className="rounded-lg"
            ></iframe>
          </motion.div>
        </motion.div>

        {/* Contact Form */}
        <motion.form
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-b from-blue-800/60 to-red-800/60 rounded-2xl p-8 shadow-2xl backdrop-blur-lg flex flex-col space-y-5"
          onSubmit={(e) => e.preventDefault()}
        >
          <h2 className="text-2xl font-semibold mb-2 border-b border-yellow-400 pb-2">
            Send Us a Message
          </h2>

          <div>
            <label className="block text-sm mb-2">Full Name</label>
            <input
              type="text"
              placeholder="Your Name"
              className="w-full p-3 rounded-lg bg-black/40 text-white border border-gray-600 focus:outline-none focus:border-yellow-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              className="w-full p-3 rounded-lg bg-black/40 text-white border border-gray-600 focus:outline-none focus:border-yellow-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Message</label>
            <textarea
              placeholder="Your message..."
              rows="5"
              className="w-full p-3 rounded-lg bg-black/40 text-white border border-gray-600 focus:outline-none focus:border-yellow-400"
              required
            />
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="w-full bg-gradient-to-r from-blue-400 to-red-600 text-black font-bold py-3 rounded-lg mt-4 shadow-lg hover:shadow-yellow-500/50 transition"
          >
            Send Message
          </motion.button>
        </motion.form>
      </div>

      {/* Footer */}
      <footer className="text-center text-gray-400 mt-12 text-sm z-10">
        © {new Date().getFullYear()} MAD Academy. All rights reserved.
      </footer>
    </main>
  );
};

export default ContactPage;
